// Single source of truth for POS access control (TSK-2997).
//
// Two-tier model:
//   Tier 1 — "till mode": mutually exclusive, till-level config (enableProduction /
//            enableProductionRMIssue / enableEstimate). A till is exactly one of
//            Production / Production RM Issue / Estimate / Normal.
//   Tier 2 — per-user permission (tillaccess.pos), only meaningful within a Normal-mode
//            till, gating /pos itself.

export const TILL_MODES = {
  PRODUCTION: "production",
  PRODUCTION_RM_ISSUE: "productionRMIssue",
  ESTIMATE: "estimate",
  NORMAL: "normal",
};

const TILL_MODE_ROUTES = {
  [TILL_MODES.PRODUCTION]: "/production",
  [TILL_MODES.PRODUCTION_RM_ISSUE]: "/production-rm-issue",
  [TILL_MODES.ESTIMATE]: "/estimate",
  [TILL_MODES.NORMAL]: "/pos",
};

// Mirrors the shouldCheckTill tri-condition already in PointOfSale/Core/index.js and
// PrivateRoute/index.js — kept flag-based (not posType-based) so this is a pure
// extraction with no behavior change for either file.
export function getTillMode(posConfig) {
  if (posConfig?.enableProduction === "Y") return TILL_MODES.PRODUCTION;
  if (posConfig?.enableProductionRMIssue === "Y") return TILL_MODES.PRODUCTION_RM_ISSUE;
  if (posConfig?.enableEstimate === "Y") return TILL_MODES.ESTIMATE;
  return TILL_MODES.NORMAL;
}

export function getTillModeRoute(mode) {
  return TILL_MODE_ROUTES[mode] || "/pos";
}

export function parseTillAccess(tillData) {
  try {
    return tillData?.tillAccess?.userAccessController ? JSON.parse(tillData.tillAccess.userAccessController) : {};
  } catch {
    return {};
  }
}

// BUG-1994: a persisted tillSession/tillData never expired identity-wise — a
// user who closes the app without logging out leaves them behind for whoever
// authenticates next. The two boot-time fast-forward gates that would
// otherwise skip straight into POS on an "active session" (App.js's
// AuthRedirect, and Till.js's BUG-1564 same-day fast-forward) call this
// first: tillDataOwner is the cookie identity captured when tillData was
// fetched (see useAuthStore.setTillData); currentIdentity is read fresh from
// cookies at the gate itself. A mismatch means the session belongs to a
// different, previously-authenticated user, so the gate must not trust it —
// it falls through to /till instead, which re-runs getTills() for whoever is
// actually logged in now and rejects them there if they have no access.
// Missing values (no owner stamped yet — older/pre-fix sessions; or no
// current identity — cookies momentarily unreadable) are treated as a match
// so this never blocks a legitimate, already-working session.
export function sessionOwnerMatchesCurrentUser(tillDataOwner, currentIdentity) {
  return !tillDataOwner || !currentIdentity || tillDataOwner === currentIdentity;
}

// production/productionRMIssue/estimate are exclusive till modes — only reachable when
// the till IS in that mode. preorder/callCenter/restaurantPreorder are additional entry
// points gated purely by their own till-level flag. pos requires both Normal mode and
// the per-user tillaccess.pos permission.
const MODULE_CHECKS = {
  pos: ({ mode, tillaccess }) => mode === TILL_MODES.NORMAL && tillaccess?.pos === "Y",
  estimate: ({ mode }) => mode === TILL_MODES.ESTIMATE,
  preorder: ({ posConfig }) => posConfig?.enablePreOrder === "Y",
  restaurantPreorder: ({ posConfig }) => posConfig?.enablePreOrder === "Y",
  callCenter: ({ posConfig }) => posConfig?.enableCallCentre === "Y",
  production: ({ mode }) => mode === TILL_MODES.PRODUCTION,
  productionRMIssue: ({ mode }) => mode === TILL_MODES.PRODUCTION_RM_ISSUE,
};

export function canAccessModule(moduleKey, { posConfig, tillaccess }) {
  const check = MODULE_CHECKS[moduleKey];
  if (!check) return false;
  const mode = getTillMode(posConfig);
  return check({ mode, posConfig, tillaccess });
}

// Where to send a user who can't access where they are / asked to go. Priority: the
// till's own mode route (production/production-rm-issue/estimate tills have no other
// legitimate screen), then for a Normal-mode till — /pos if permitted, else /preorder,
// else /call-center, else /sign-in (no legitimate destination).
export function pickDefaultRoute({ posConfig, tillaccess }) {
  const mode = getTillMode(posConfig);
  if (mode !== TILL_MODES.NORMAL) {
    return getTillModeRoute(mode);
  }
  if (tillaccess?.pos === "Y") return "/pos";
  if (posConfig?.enablePreOrder === "Y") return "/preorder";
  if (posConfig?.enableCallCentre === "Y") return "/call-center";
  return "/sign-in";
}
